import type { GridApi, IRowNode } from 'ag-grid-enterprise';

export type TreeNodePredicate<TData> = (node: IRowNode<TData>) => boolean;

export interface TreeExpansionSnapshot<TData> {
  /** collapsed group nodes currently visible under the root */
  revealedFrontier: IRowNode<TData>[];
  /** revealed expanded nodes that have no expanded descendant of their own */
  deepestExpandedFrontier: IRowNode<TData>[];
  /** every expanded group node below the root, revealed or not */
  expandedSubtree: IRowNode<TData>[];
}

interface RootRelation {
  inSubtree: boolean;
  revealed: boolean;
}

type RelationCache<TData> = Map<IRowNode<TData>, RootRelation>;

const OUTSIDE_ROOT: RootRelation = { inSubtree: false, revealed: false };

function resolveRootRelation<TData>(
  node: IRowNode<TData>,
  root: IRowNode<TData>,
  cache: RelationCache<TData>,
): RootRelation {
  const pending: IRowNode<TData>[] = [];
  let current: IRowNode<TData> | null = node;
  let relation: RootRelation | undefined;

  while (current) {
    const cached = cache.get(current);
    if (cached) {
      relation = cached;
      break;
    }
    if (current === root) {
      relation = { inSubtree: true, revealed: true };
      cache.set(current, relation);
      break;
    }
    pending.push(current);
    current = current.parent;
  }
  relation ??= OUTSIDE_ROOT;

  for (let index = pending.length - 1; index >= 0; index--) {
    const child = pending[index];
    relation = {
      inSubtree: relation.inSubtree,
      revealed: relation.revealed && Boolean(child.parent?.expanded),
    };
    cache.set(child, relation);
  }
  return relation;
}

function keepDeepest<TData>(expanded: IRowNode<TData>[]): IRowNode<TData>[] {
  const hasExpandedDescendant = new Set<IRowNode<TData>>();
  for (const node of expanded) {
    let parent = node.parent;
    while (parent && !hasExpandedDescendant.has(parent)) {
      hasExpandedDescendant.add(parent);
      parent = parent.parent;
    }
  }
  return expanded.filter((node) => !hasExpandedDescendant.has(node));
}

export function collectRevealedFrontier<TData>(
  api: GridApi<TData>,
  root: IRowNode<TData>,
  isGroupNode: TreeNodePredicate<TData>,
): IRowNode<TData>[] {
  const cache: RelationCache<TData> = new Map();
  const frontier: IRowNode<TData>[] = [];
  api.forEachNode((node) => {
    if (!isGroupNode(node) || node.expanded) return;
    if (resolveRootRelation(node, root, cache).revealed) frontier.push(node);
  });
  return frontier;
}

export function collectTreeExpansionSnapshot<TData>(
  api: GridApi<TData>,
  root: IRowNode<TData>,
  isGroupNode: TreeNodePredicate<TData>,
): TreeExpansionSnapshot<TData> {
  const cache: RelationCache<TData> = new Map();
  const revealedFrontier: IRowNode<TData>[] = [];
  const revealedExpanded: IRowNode<TData>[] = [];
  const expandedSubtree: IRowNode<TData>[] = [];

  api.forEachNode((node) => {
    if (!isGroupNode(node)) return;
    const relation = resolveRootRelation(node, root, cache);
    if (!relation.inSubtree) return;
    if (!node.expanded) {
      if (relation.revealed) revealedFrontier.push(node);
      return;
    }
    if (relation.revealed) revealedExpanded.push(node);
    if (node !== root) expandedSubtree.push(node);
  });

  return {
    revealedFrontier,
    deepestExpandedFrontier: keepDeepest(revealedExpanded),
    expandedSubtree,
  };
}
